import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './util';

interface FieldShellProps {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  counter?: ReactNode;
  children: ReactNode;
  className?: string;
}

function FieldShell({ id, label, hint, error, counter, children, className }: FieldShellProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {(label || counter) && (
        <div className="flex items-baseline justify-between gap-2">
          {label && (
            <label htmlFor={id} className="comic text-[19px]">
              {label}
            </label>
          )}
          {counter && (
            <span className="text-[12px] font-extrabold text-ink-soft tabular-nums">{counter}</span>
          )}
        </div>
      )}
      {children}
      {error ? (
        <p
          id={`${id}-msg`}
          role="alert"
          className="flex items-center gap-1 text-[13px] font-extrabold text-team-b"
        >
          <span aria-hidden>✖</span> {error}
        </p>
      ) : hint ? (
        <p id={`${id}-msg`} className="text-[13px] font-semibold text-ink-soft">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const FIELD = cn(
  'w-full border-3 border-ink bg-white px-3 font-extrabold text-ink shadow-ink-sm placeholder:font-semibold placeholder:text-[#a59e8c]',
  'transition-[box-shadow,translate] duration-100 outline-none',
  'focus:-translate-x-px focus:-translate-y-px focus:bg-[#fffdf3] focus:shadow-ink',
  'disabled:bg-[#eee8d8] disabled:text-muted disabled:shadow-none',
);

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Show "n/maxLength" next to the label. */
  showCount?: boolean;
  /** Big Bangers input (room code, name on the home page). */
  size?: 'md' | 'lg';
  /** Element rendered inside the field on the left (emoji, icon). */
  leading?: ReactNode;
  ref?: Ref<HTMLInputElement>;
  wrapperClassName?: string;
}

export function TextInput({
  label,
  hint,
  error,
  showCount,
  size = 'md',
  leading,
  id: idProp,
  className,
  wrapperClassName,
  maxLength,
  value,
  ref,
  ...rest
}: TextInputProps) {
  const auto = useId();
  const id = idProp ?? auto;
  const count = showCount && maxLength ? `${String(value ?? '').length}/${maxLength}` : undefined;
  return (
    <FieldShell
      id={id}
      label={label}
      hint={hint}
      error={error}
      counter={count}
      className={wrapperClassName}
    >
      <div className="relative">
        {leading && (
          <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[20px] leading-none">
            {leading}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          value={value}
          maxLength={maxLength}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? `${id}-msg` : undefined}
          className={cn(
            FIELD,
            size === 'lg' ? 'comic h-14 text-[28px] tracking-[0.08em]' : 'h-11 text-[16px]',
            leading && 'pl-11',
            error && 'border-team-b bg-team-b-tint',
            className,
          )}
          {...rest}
        />
      </div>
    </FieldShell>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  showCount?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
  wrapperClassName?: string;
}

export function TextArea({
  label,
  hint,
  error,
  showCount,
  id: idProp,
  className,
  wrapperClassName,
  maxLength,
  value,
  rows = 6,
  ref,
  ...rest
}: TextAreaProps) {
  const auto = useId();
  const id = idProp ?? auto;
  const count = showCount && maxLength ? `${String(value ?? '').length}/${maxLength}` : undefined;
  return (
    <FieldShell
      id={id}
      label={label}
      hint={hint}
      error={error}
      counter={count}
      className={wrapperClassName}
    >
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        value={value}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${id}-msg` : undefined}
        className={cn(
          FIELD,
          // 16px on phones: iOS zooms the page into any smaller focused field.
          'resize-y py-2 text-[16px] leading-relaxed sm:text-[15px]',
          // ruled notebook lines, comic-paper feel
          'bg-[linear-gradient(transparent_calc(1.625em_-_1px),rgb(17_17_17/0.08)_calc(1.625em_-_1px))] bg-[length:100%_1.625em] bg-[position:0_0.5rem] bg-local',
          error && 'border-team-b',
          className,
        )}
        {...rest}
      />
    </FieldShell>
  );
}
